// Interpret the hint protocol into typed UI prop bags. The widget UI
// reads from these helpers, never from raw `LLMResponse.hint`.
// Server contract: berrybot/app/conversations/hints.py.

import type {
  ContactDetailsInputHint,
  DeliveryAddressInputHint,
  ExtrasChoiceHint,
  FulfillmentInputHint,
  Hint,
  ItemsSuggestionHint,
  ModifierChoiceHint,
  QuantityConfirmHint,
  ScheduledTimeConfirmHint,
  SubmitConfirmHint,
} from './types'

export type HintKind =
  | 'fulfillment'
  | 'delivery_address'
  | 'contact_details'
  | 'submit'
  | 'quantity_confirm'
  | 'scheduled_time'
  | 'modifiers'
  | 'extras'
  | 'suggestions'
  | null

// One blocking hint per turn; suggestions are non-blocking and stash
// alongside whatever blocking hint is active. The server already
// prioritises blocking > suggestions in build_hint, so we trust the
// payload's `type` and only need to recognise the four shapes.
export function classify(hint: Hint | null | undefined): HintKind {
  if (!hint) return null
  if (hint.type === 'input_needed' && hint.subject === 'fulfillment_type') {
    return 'fulfillment'
  }
  if (hint.type === 'input_needed' && hint.subject === 'delivery_address') {
    return 'delivery_address'
  }
  if (hint.type === 'input_needed' && hint.subject === 'contact_details') {
    return 'contact_details'
  }
  if (hint.type === 'confirmation_needed' && hint.subject === 'submit_order') {
    return 'submit'
  }
  if (hint.type === 'confirmation_needed' && hint.subject === 'add_quantity') {
    return 'quantity_confirm'
  }
  if (hint.type === 'confirmation_needed' && hint.subject === 'scheduled_time') {
    return 'scheduled_time'
  }
  if (hint.type === 'choice_needed' && hint.subject === 'modifiers') {
    return 'modifiers'
  }
  if (hint.type === 'choice_needed' && hint.subject === 'extras') {
    return 'extras'
  }
  if (hint.type === 'suggestions' && hint.subject === 'items') {
    return 'suggestions'
  }
  return null
}

export function isBlocking(hint: Hint | null | undefined): boolean {
  const k = classify(hint)
  return k === 'fulfillment' || k === 'delivery_address'
    || k === 'contact_details'
    || k === 'submit' || k === 'modifiers' || k === 'quantity_confirm'
    || k === 'scheduled_time'
}

// Narrowing accessors. Each returns null when the hint isn't of that
// kind, so consumers can write:
//     if (const h = asFulfillment(hint)) renderFulfillmentPicker(h)

export function asFulfillment(
  hint: Hint | null | undefined,
): FulfillmentInputHint | null {
  return classify(hint) === 'fulfillment' ? (hint as FulfillmentInputHint) : null
}

export function asDeliveryAddress(
  hint: Hint | null | undefined,
): DeliveryAddressInputHint | null {
  return classify(hint) === 'delivery_address'
    ? (hint as DeliveryAddressInputHint)
    : null
}

export function asContactDetails(
  hint: Hint | null | undefined,
): ContactDetailsInputHint | null {
  return classify(hint) === 'contact_details'
    ? (hint as ContactDetailsInputHint)
    : null
}

export function asSubmit(
  hint: Hint | null | undefined,
): SubmitConfirmHint | null {
  return classify(hint) === 'submit' ? (hint as SubmitConfirmHint) : null
}

export function asQuantityConfirm(
  hint: Hint | null | undefined,
): QuantityConfirmHint | null {
  return classify(hint) === 'quantity_confirm'
    ? (hint as QuantityConfirmHint)
    : null
}

export function asScheduledTime(
  hint: Hint | null | undefined,
): ScheduledTimeConfirmHint | null {
  return classify(hint) === 'scheduled_time'
    ? (hint as ScheduledTimeConfirmHint)
    : null
}

export function asModifiers(
  hint: Hint | null | undefined,
): ModifierChoiceHint | null {
  return classify(hint) === 'modifiers' ? (hint as ModifierChoiceHint) : null
}

export function asExtras(
  hint: Hint | null | undefined,
): ExtrasChoiceHint | null {
  return classify(hint) === 'extras' ? (hint as ExtrasChoiceHint) : null
}

export function asSuggestions(
  hint: Hint | null | undefined,
): ItemsSuggestionHint | null {
  return classify(hint) === 'suggestions' ? (hint as ItemsSuggestionHint) : null
}
